import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, ShieldCheck, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Reveal } from "@/components/effects/Reveal";
import { LightStreaks } from "@/components/effects/LightStreaks";
import { BokehParticles } from "@/components/effects/BokehParticles";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

function validate(pw, confirm) {
  if (!pw || pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[0-9]/.test(pw)) return "Password must include at least one number.";
  if (!/[a-zA-Z]/.test(pw)) return "Password must include at least one letter.";
  if (pw !== confirm) return "Passwords do not match.";
  return null;
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { role }
  const [error, setError] = useState("");
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const localValidation = useMemo(() => (pw || confirm) ? validate(pw, confirm) : null, [pw, confirm]);
  const canSubmit = token && pw && confirm && !localValidation && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const msg = validate(pw, confirm);
    if (msg) { setError(msg); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/reset-password`, {
        token, new_password: pw, confirm_password: confirm,
      });
      setDone({ role: r.data.role || "customer" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 && typeof detail === "string" && detail.toLowerCase().includes("expired")) {
        setTokenInvalid(true);
      } else if (status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
      } else {
        setError(typeof detail === "string" ? detail : "We could not reset your password. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const targetLogin = done?.role === "admin" ? "/admin/login" : "/auth";

  return (
    <div data-testid="page-reset-password" className="relative min-h-[100svh] overflow-hidden flex items-center justify-center py-16">
      <SEO title="Reset Password · WelkomBiz Photography" description="Set a new password for your WelkomBiz Photography account." canonicalPath="/reset-password" noindex />
      <div className="absolute inset-0 -z-10" style={{
        background:
          "radial-gradient(70% 60% at 50% 0%, rgba(255,46,147,0.12) 0%, transparent 60%)," +
          "radial-gradient(70% 60% at 50% 100%, rgba(24,211,255,0.10) 0%, transparent 60%)," +
          "linear-gradient(180deg, #000, #050507 50%, #000)",
      }} />
      <LightStreaks count={4} />
      <BokehParticles count={10} />

      <div className="relative w-full max-w-[480px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-7">
            <Logo size={56} linkTo="/" />
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass chrome-border">
              <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
              <span className="text-[10px] tracking-luxe uppercase text-white/70">Secure Reset</span>
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl">
              <span className="text-chrome">Set a new </span>
              <span className="italic text-magcyan">password</span>
              <span className="text-chrome">.</span>
            </h1>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <div className="glass-strong chrome-border rounded-md p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />

            {!token || tokenInvalid ? (
              <div data-testid="reset-password-invalid" className="relative text-center space-y-3 py-2">
                <div className="w-12 h-12 rounded-full bg-[#ffd479]/20 border border-[#ffd479]/40 mx-auto flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#ffd479]" />
                </div>
                <h2 className="font-display text-xl text-chrome">Reset link expired</h2>
                <p className="text-sm text-white/65 font-body">
                  This reset link is invalid or has expired. Please request a new password reset link.
                </p>
                <Link to="/forgot-password" data-testid="reset-password-request-new" className="btn-primary !py-2.5 !px-5 !text-[11px] mt-3 inline-flex">
                  Request a new link <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ) : done ? (
              <div data-testid="reset-password-success" className="relative text-center space-y-3 py-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff2e93] to-[#18d3ff] mx-auto flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <h2 className="font-display text-xl text-chrome">Password reset</h2>
                <p className="text-sm text-white/65 font-body">
                  Your password has been reset successfully. You can now log in with your new password.
                </p>
                <button data-testid="reset-password-goto-login" onClick={() => navigate(targetLogin)} className="btn-primary !py-2.5 !px-5 !text-[11px] mt-3 inline-flex">
                  Go to login <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="reset-password-form" className="relative space-y-4">
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
                    <Lock className="w-3 h-3" /> New Password
                  </span>
                  <input
                    data-testid="reset-password-new"
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters with letters + a number"
                    className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
                    <Lock className="w-3 h-3" /> Confirm New Password
                  </span>
                  <input
                    data-testid="reset-password-confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter the same password"
                    className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
                  />
                </label>

                {(localValidation || error) && (
                  <p data-testid="reset-password-error" className="text-xs text-[#ff9fcf]">
                    {error || localValidation}
                  </p>
                )}

                <button type="submit" data-testid="reset-password-submit" disabled={!canSubmit} className="btn-primary w-full mt-2 disabled:opacity-50">
                  {busy ? "Resetting…" : "Reset Password"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>

                <div className="text-center pt-1">
                  <Link to="/auth" data-testid="reset-password-back" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
